import React, { useState } from 'react';
import { Loader2, UserCheck, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const EMPTY = { payer_name: '', payer_document: '', payer_phone: '', payer_email: '' };

/** Datos de quien paga la suscripción. Se guardan para los próximos meses y se pueden cambiar. */
export function PayerForm({ payer, churchId, onSaved }) {
  const [editing, setEditing] = useState(!payer);
  const [form, setForm] = useState(payer || EMPTY);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/billing/profile', form, { params: { church_id: churchId } });
      toast.success('Datos de pago guardados');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder, type = 'text') => (
    <div className="space-y-1.5">
      <Label htmlFor={key} className="text-xs">{label}</Label>
      <Input id={key} type={type} value={form[key] || ''} placeholder={placeholder}
             onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
             data-testid={`payer-${key}`} />
    </div>
  );

  return (
    <Card data-testid="payer-form">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base md:text-lg flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-[#056A84]" /> Quién paga
        </CardTitle>
        {!editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}
                  data-testid="payer-edit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Cambiar
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!editing ? (
          <div className="text-sm text-muted-foreground" data-testid="payer-summary">
            <p className="text-foreground font-medium">{payer?.payer_name}</p>
            <p>{payer?.payer_document} · {payer?.payer_phone}</p>
            <p>{payer?.payer_email}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              {field('payer_name', 'Nombre completo', 'Como aparece en la cédula')}
              {field('payer_document', 'Cédula o RIF', 'V12345678')}
              {field('payer_phone', 'Teléfono', '04141234567')}
              {field('payer_email', 'Correo', 'pastor@iglesia.com', 'email')}
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving}
                      className="bg-[#056A84] hover:bg-[#045A70] text-white"
                      data-testid="payer-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </Button>
              {payer && (
                <Button variant="ghost" onClick={() => { setForm(payer); setEditing(false); }}>
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PayerForm;
